import { LineChart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPercent } from '@/lib/format-utils'
import { getDrBaseline, getDrBaselineHistory } from '@/lib/dr-utils'
import { ROAS_STANDARD_DAYS } from '@/features/roas/constants/roas-days'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  formatRoasDayLabel,
  getDayRoasFieldName,
  getPrevDayRoasFieldName,
  isWeeklyRoasValueVisible,
} from './day-roas-utils'

export function renderBaselineChangeTooltipLines(change) {
  const lines = []

  ROAS_STANDARD_DAYS.forEach((day) => {
    const entry = change[`day${day}`]
    if (entry) {
      lines.push(
        <p key={`day${day}`} className="pl-2">
          DR Day {day}: {entry.from} -&gt; {entry.to}
        </p>,
      )
    }
  })

  if (change.cost) {
    lines.push(
      <p key="cost" className="pl-2">
        Cost: {change.cost.from} -&gt; {change.cost.to}
      </p>,
    )
  }

  if (change.profit) {
    lines.push(
      <p key="profit" className="pl-2">
        Profit: {change.profit.from} -&gt; {change.profit.to}
      </p>,
    )
  }

  return lines
}

export const RoasHeader = ({
  day,
  drBaselines,
  country,
  pid,
  packageName = null,
  onDayClick = null,
}) => {
  const {
    value: headerBaseline,
    isDefault,
    sourcePid,
    sourceMediaSource,
    isGroupFallback,
    isMediaFallback,
    isCountryFallback,
  } = getDrBaseline(drBaselines, day, country, pid, packageName)
  const formatted = formatPercent(headerBaseline, 0, 0)
  const history = getDrBaselineHistory(
    drBaselines,
    day,
    country,
    pid,
    packageName,
  )

  const sourceHints = []
  if (isGroupFallback && sourcePid) {
    sourceHints.push(`Using group baseline ${sourcePid}`)
  }
  if (isMediaFallback && sourceMediaSource) {
    sourceHints.push(`Using channel fallback ${sourceMediaSource}`)
  }
  if (isCountryFallback) {
    sourceHints.push('Using overall country fallback')
  }

  const tooltipContent =
    history.length > 0 || sourceHints.length > 0 ? (
      <div className="space-y-1">
        {sourceHints.map((hint) => (
          <p key={hint} className="text-xs text-amber-600">
            {hint}
          </p>
        ))}
        {history.length > 0 && (
          <>
            <p className="font-medium">Baseline History:</p>
            {history.map((record, index) => (
              <p key={record.date} className="text-xs">
                {record.date}: {formatPercent(record.value, 0, 0)}
                {index === 0 && ' (current)'}
              </p>
            ))}
          </>
        )}
      </div>
    ) : (
      <p className="text-xs">No baseline configuration found</p>
    )

  if (onDayClick) {
    return (
      <div className="flex items-center justify-end gap-0.5 text-right whitespace-nowrap">
        <button
          type="button"
          className="inline-flex items-center gap-0.5 font-inherit hover:text-primary"
          onClick={() => onDayClick(day)}
        >
          <span>{formatRoasDayLabel(day)}</span>
          <LineChart className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </button>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'text-xs cursor-help',
                  !isDefault && 'text-green-600 font-semibold',
                )}
              >
                ({formatted})
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="text-right cursor-help">
            {formatRoasDayLabel(day)}
            <span
              className={cn(
                'text-xs ml-1',
                !isDefault && 'text-green-600 font-semibold',
              )}
            >
              ({formatted})
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const getRoasCellColorClass = (roas, cellBaseline) => {
  if (!cellBaseline || cellBaseline <= 0) return ''
  return roas < cellBaseline ? 'text-destructive' : 'text-primary'
}

export function createWeeklyDayRoasColumn(
  day,
  drBaselines,
  country,
  pid,
  packageName,
  Td3,
  onDayClick = null,
) {
  const roasField = getDayRoasFieldName(day)
  const prevField = getPrevDayRoasFieldName(day, 'weekly')

  return {
    id: roasField,
    accessorFn: (row) => {
      const roas = row[roasField]
      const preRoas = row[prevField]
      const roasVisible = isWeeklyRoasValueVisible(roas)
      const preRoasVisible = isWeeklyRoasValueVisible(preRoas)
      const growth =
        roasVisible && preRoasVisible && preRoas !== 0
          ? (roas - preRoas) / preRoas
          : undefined
      const absoluteDiff =
        roasVisible && preRoasVisible ? roas - preRoas : undefined
      return { roas, growth, week: row.week, absoluteDiff, roasVisible }
    },
    header: () => (
      <RoasHeader
        day={day}
        drBaselines={drBaselines}
        country={country}
        pid={pid}
        packageName={packageName}
        onDayClick={onDayClick}
      />
    ),
    cell: (row) => {
      const { roas, growth, absoluteDiff, roasVisible } = row.getValue()
      const { value: cellBaseline } = getDrBaseline(
        drBaselines,
        day,
        country,
        pid,
        packageName,
      )
      const formatted = roasVisible ? formatPercent(roas) : '-'
      return (
        <div
          className={cn(
            'font-bold text-right',
            roasVisible ? getRoasCellColorClass(roas, cellBaseline) : '',
          )}
        >
          <Td3
            avg="-"
            growth={growth}
            absoluteDiff={absoluteDiff}
            formatType="percent"
          >
            {formatted}
          </Td3>
        </div>
      )
    },
  }
}

export function createMonthlyDayRoasColumn(
  day,
  drBaselines,
  country,
  pid,
  packageName,
  Td3,
) {
  const roasField = getDayRoasFieldName(day)
  const prevField = getPrevDayRoasFieldName(day, 'monthly')

  return {
    id: roasField,
    accessorKey: roasField,
    header: () => (
      <RoasHeader
        day={day}
        drBaselines={drBaselines}
        country={country}
        pid={pid}
        packageName={packageName}
      />
    ),
    cell: ({ row }) => {
      const roas = row.getValue(roasField)
      const roasVisible = isWeeklyRoasValueVisible(roas)
      const { value: cellBaseline } = getDrBaseline(
        drBaselines,
        day,
        country,
        pid,
        packageName,
      )
      const prevRoas = row.original[prevField]
      const prevRoasVisible = isWeeklyRoasValueVisible(prevRoas)
      const growth =
        roasVisible && prevRoasVisible && prevRoas !== 0
          ? (roas - prevRoas) / prevRoas
          : undefined
      const absoluteDiff =
        roasVisible && prevRoasVisible ? roas - prevRoas : undefined
      const formatted = roasVisible ? formatPercent(roas) : '-'
      return (
        <div
          className={cn(
            'font-bold text-right',
            roasVisible ? getRoasCellColorClass(roas, cellBaseline) : '',
          )}
        >
          <Td3
            avg="-"
            growth={growth}
            absoluteDiff={absoluteDiff}
            formatType="percent"
          >
            {formatted}
          </Td3>
        </div>
      )
    },
  }
}

export function createAllWeeklyDayRoasColumns(
  drBaselines,
  country,
  pid,
  packageName,
  Td3,
  onDayClick = null,
  days = ROAS_STANDARD_DAYS,
) {
  return days.map((day) =>
    createWeeklyDayRoasColumn(
      day,
      drBaselines,
      country,
      pid,
      packageName,
      Td3,
      onDayClick,
    ),
  )
}

export function createAllMonthlyDayRoasColumns(
  drBaselines,
  country,
  pid,
  packageName,
  Td3,
) {
  return ROAS_STANDARD_DAYS.map((day) =>
    createMonthlyDayRoasColumn(day, drBaselines, country, pid, packageName, Td3),
  )
}
